/**
 * GroupAttendanceChart.js
 * Visualizes attendance percentage over time for a group
 */

import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import 'moment/locale/it';
import { Card, Badge, OverlayTrigger, Tooltip as BsTooltip, Dropdown } from 'react-bootstrap';

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

moment.locale('it');

const GroupAttendanceChart = ({ group, onPointClick }) => {
  const chartRef = useRef(null);

  if (!group) return null;

  const points = group.points || [];
  const hasRollCalls = points.length > 0;

  // Format labels for X axis
  const labels = points.map((p) => moment(p.date).format('DD/MM/YY'));
  const dataValues = points.map((p) => p.attendanceRate);

  const chartData = {
    labels,
    datasets: [
      {
        label: '% Partecipazione',
        data: dataValues,
        fill: true,
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.12)',
        pointBackgroundColor: '#0d6efd',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 9,
        pointHitRadius: 25,
        pointHoverBackgroundColor: '#0b5ed7',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        tension: 0.25,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    onClick: (event, elements, chart) => {
      if (!onPointClick || !hasRollCalls) return;

      let targetIndex = -1;

      // 1. Direct hit from Chart.js elements
      if (elements && elements.length > 0) {
        targetIndex = elements[0].index;
      } else {
        // 2. Query nearest element using the native event
        const chartInstance = chart || chartRef.current?.chart || chartRef.current;
        const nativeEvt = event?.native || event?.nativeEvent || event;
        if (chartInstance && nativeEvt && typeof chartInstance.getElementsAtEventForMode === 'function') {
          const nearest = chartInstance.getElementsAtEventForMode(
            nativeEvt,
            'nearest',
            { intersect: false, axis: 'x' },
            false
          );
          if (nearest && nearest.length > 0) {
            targetIndex = nearest[0].index;
          }
        }
      }

      if (targetIndex >= 0 && points[targetIndex]) {
        onPointClick(group.groupId, points[targetIndex].date);
      }
    },
    onHover: (event, activeElements) => {
      const target = event?.native?.target || event?.target;
      if (target && target.style) {
        target.style.cursor =
          activeElements && activeElements.length > 0 ? 'pointer' : 'default';
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          stepSize: 20,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.06)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          title: (context) => {
            const index = context[0]?.dataIndex;
            const p = points[index];
            return p ? moment(p.date).format('dddd DD MMMM YYYY') : '';
          },
          label: (context) => {
            const index = context.dataIndex;
            const p = points[index];
            if (!p) return '';
            return [
              `Partecipazione: ${p.attendanceRate}%`,
              `Presenti: ${p.presentCount} su ${p.totalEnrolled}`,
              `Assenti: ${p.absentCount}`,
            ];
          },
          footer: () => '👆 Fai clic per aprire l\'appello di questa data',
        },
      },
    },
  };

  const getBadgeVariant = (rate) => {
    if (!hasRollCalls) return 'secondary';
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'danger';
  };

  return (
    <Card className="h-100 shadow-sm border-0 group-stat-card">
      <Card.Header className="bg-white border-bottom-0 pt-3 pb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h5 className="mb-0 fw-bold text-dark">
            <i className="fas fa-users-cog text-primary me-2"></i>
            {group.groupName}
          </h5>
          <small className="text-muted">
            {hasRollCalls ? `${group.totalRollCalls} appelli registrati` : 'Nessun appello'}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {hasRollCalls && (
            <OverlayTrigger
              placement="top"
              overlay={
                <BsTooltip id={`tooltip-presenti-${group.groupId}`}>
                  Totale presenze registrate: {group.totalPresent} su {group.totalExpected} presenze attese
                </BsTooltip>
              }
            >
              <Badge bg="light" text="dark" className="border">
                <i className="fas fa-user-check me-1 text-success"></i>
                {group.totalPresent}/{group.totalExpected}
              </Badge>
            </OverlayTrigger>
          )}
          <Badge bg={getBadgeVariant(group.averageAttendanceRate)} className="px-2 py-1 fs-6">
            <i className="fas fa-percent me-1"></i>
            {hasRollCalls ? `${group.averageAttendanceRate}% media` : 'N/D'}
          </Badge>
          {hasRollCalls && (
            <Dropdown align="end">
              <Dropdown.Toggle
                size="sm"
                variant="outline-primary"
                className="py-1 px-2 border"
                style={{ fontSize: '0.8rem' }}
                title="Scegli una data per aprire il relativo appello"
              >
                <i className="fas fa-calendar-check me-1"></i> Appelli
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: '260px', overflowY: 'auto' }}>
                <Dropdown.Header>Seleziona un appello da aprire:</Dropdown.Header>
                {points.slice().reverse().map((p) => (
                  <Dropdown.Item
                    key={p.date}
                    onClick={() => onPointClick && onPointClick(group.groupId, p.date)}
                  >
                    <span className="fw-semibold">{moment(p.date).format('DD/MM/YYYY')}</span>
                    <span className="text-muted ms-2 small">
                      {p.attendanceRate}% ({p.presentCount}/{p.totalEnrolled})
                    </span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </Card.Header>
      <Card.Body className="pt-1 pb-3">
        {hasRollCalls ? (
          <div style={{ height: '220px', position: 'relative', cursor: 'pointer' }} title="Fai clic su una data per visualizzare l'appello">
            <Line ref={chartRef} data={chartData} options={options} />
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5 my-2">
            <i className="fas fa-calendar-times fa-3x mb-2 text-black-50"></i>
            <span>Nessun appello registrato nel periodo selezionato</span>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default GroupAttendanceChart;
